/**
 * お肌管理 個別施術ページの「正式コンテンツ」データ。
 *
 * NOTE（重要）:
 *   ・存在しない情報は作成しない。
 *   ・未確定の施術時間・ダウンタイム・推奨回数・推奨間隔・製剤量は推測しない（「—」を維持）。
 *   ・医療効果を断定する表現（必ず改善する / 美白できる / 若返る 等）は使用しない。
 *   ・他院の施術時間・ダウンタイム・機器名は転用しない。
 *   ・過去に使用していた旧名称・確認できていない製剤名／機器名は使用しない。
 *   ・料金は SBDC の正式料金のみを使用する。
 *
 * 施術体系:
 *   01 ヴィーナス ステムサップ   = PLASMA / プラズマ / StemSup（幹細胞培養上清液）
 *   02 ヴィーナス ダイヤモンド   = PLASMA / プラズマ / リジュラン
 *   03 シンデレラ ステムサップ   = DERMAPEN 4 / ダーマペン4 / StemSup（幹細胞培養上清液）
 *   04 シンデレラ ダイヤモンド   = DERMAPEN 4 / ダーマペン4 / リジュラン
 *   05 ヴェルヴェットスキン     = DERMAPEN 4 / ダーマペン4＋コラーゲンピール
 *   06 ラクトピール             = PEELING / 乳酸を使用したケミカルピーリング
 *   07 コラーゲンピール         = PEELING
 *   08 MIXピール                = PEELING
 *   09 プラズマ                 = PLASMA
 *   10 ホワイトフォトフェイシャル = PHOTO FACIAL
 *
 * 未確定の値は「—」で表示し、後から正式値へ差し替えられるようにしている。
 *
 * ---------------------------------------------------------------------------
 * 正式情報が確定したときの差し替え手順（表示ロジックには触れず、このファイルの値だけを更新する）
 * ---------------------------------------------------------------------------
 *   ・施術時間／麻酔／ダウンタイム／推奨回数／推奨間隔
 *       → 各施術の spec(...) に、確定した項目だけを指定する。
 *         例: spec({ duration: "30分", anesthesia: "麻酔クリーム（別料金）" })
 *         未確定の項目は省略すれば PENDING（「—」）のまま表示される。
 *   ・使用機器（09 プラズマ / 10 ホワイトフォトフェイシャル）
 *       → method の { label: "使用機器", value: "確認中" } を正式な機器名に置き換える。
 *         機器名が未確定の間は「確認中」および methodNote を維持する。
 *   ・料金
 *       → price.rows を、施術ごとの「1回の定価」に置き換える（複数回コースはWeb非掲載）。
 *   ・いずれの場合も、存在しない情報・推測値・他院の情報は追加しない。
 * ---------------------------------------------------------------------------
 */

export type SkinDetailFeature = { no: string; title: string; text: string };
export type SkinDetailSpec = { label: string; value: string };

/** 料金表の1行（1回の定価）。複数回コースはWeb非掲載。 */
export type SkinDetailPriceRow = { label: string; price: string };
export type SkinDetailPrice = { rows: SkinDetailPriceRow[]; note?: string };
export type SkinDetailFaq = { q: string; a: string };

export type SkinTreatmentDetail = {
  /** ABOUT（この施術について）の段落 */
  about: string[];
  /** ABOUT の補足（誤解を招かないための注意書きなど） */
  aboutNote?: string;
  /** 施術の特徴（未確定の施術は未設定＝プレースホルダーを維持） */
  features?: SkinDetailFeature[];
  /** 施術情報（分類 / 施術方法 / 使用製剤・機器） */
  method: SkinDetailSpec[];
  methodNote?: string;
  /** 施術時間・ダウンタイム等（未確定は「—」） */
  spec: SkinDetailSpec[];
  /** 正式料金（未確定の施術は未設定） */
  price?: SkinDetailPrice;
  /** 料金が準備中である旨などの補足 */
  priceNote?: string;
  /** リスク・副作用・注意事項（未確定の施術は未設定＝プレースホルダーを維持） */
  risks?: string[];
  riskNote?: string;
  /** よくあるご質問（未設定の施術は汎用FAQを使用） */
  faqs?: SkinDetailFaq[];
};

const PENDING = "—";

/**
 * 施術時間・麻酔・ダウンタイム・推奨回数・推奨間隔（TIME / DOWNTIME）の5項目。
 *
 * 正式情報が確定したら、該当項目を「—」から正式値へ置き換えるだけでよい。
 * 未確定の項目は省略でき、その場合は PENDING（「—」）で表示される。
 * 項目は常に「施術時間 → 麻酔 → ダウンタイム → 推奨回数 → 推奨間隔」の順で出力される。
 */
type SpecFields = {
  /** 施術時間。未確定は PENDING。各施術のコメントを参照。 */
  duration?: string;
  /** 麻酔。未確定は PENDING（例: "麻酔クリーム（別料金）"）。 */
  anesthesia?: string;
  /** ダウンタイム。未確定は PENDING。各施術のコメントを参照。 */
  downtime?: string;
  /** 推奨回数。未確定は PENDING。各施術のコメントを参照。 */
  sessions?: string;
  /** 推奨間隔。未確定は PENDING。各施術のコメントを参照。 */
  interval?: string;
};

/** 上記5項目を、全施術ページ共通の順序・同じ情報密度で組み立てる。 */
const spec = (fields: SpecFields = {}): SkinDetailSpec[] => [
  { label: "施術時間", value: fields.duration ?? PENDING },
  { label: "麻酔", value: fields.anesthesia ?? PENDING },
  { label: "ダウンタイム", value: fields.downtime ?? PENDING },
  { label: "推奨回数", value: fields.sessions ?? PENDING },
  { label: "推奨間隔", value: fields.interval ?? PENDING },
];

export const skinTreatmentDetails: Record<string, SkinTreatmentDetail> = {
  /* ============================== 01 ヴィーナス ステムサップ ============================== */
  "venus-stemsup": {
    about: [
      "ヴィーナス ステムサップは、プラズマとStemSup（幹細胞培養上清液）を組み合わせたSBDCのお肌管理メニューです。",
      "お肌の状態やお悩みを確認し、医師の診察のもと施術をご案内します。",
    ],
    aboutNote: "StemSupは、生きた幹細胞を移植する治療ではありません。幹細胞培養上清液を使用した美容医療です。",
    features: [
      {
        no: "01",
        title: "プラズマとStemSupを組み合わせた施術",
        text: "プラズマとStemSup（幹細胞培養上清液）を組み合わせたお肌管理メニューです。",
      },
      {
        no: "02",
        title: "肌状態やお悩みに合わせてご案内",
        text: "お肌の状態やお悩みを確認し、施術内容をご案内します。",
      },
      {
        no: "03",
        title: "医師の診察のもとで施術",
        text: "医師の診察のもとで施術を行います。",
      },
    ],
    method: [
      { label: "分類", value: "PLASMA" },
      { label: "施術方法", value: "プラズマ" },
      { label: "使用製剤", value: "StemSup（幹細胞培養上清液）" },
    ],
    // 01 ヴィーナス ステムサップ: 施術時間／麻酔／ダウンタイム／推奨回数／推奨間隔／料金は未確定（「—」を維持）
    spec: spec(),
    price: {
      rows: [
        { label: "脂肪幹細胞由来培養上清液（StemSup-A）", price: "99,000円" },
        { label: "歯髄幹細胞由来培養上清液（StemSup-DP）", price: "99,000円" },
        { label: "臍帯幹細胞由来培養上清液（StemSup-UC）", price: "143,000円" },
      ],
    },
    risks: [
      "施術後に赤み、熱感、刺激感、乾燥などが生じる場合があります。",
      "また、使用する製剤や肌状態によって、一時的な炎症や肌トラブルが生じる可能性があります。",
      "症状や経過には個人差があります。",
    ],
    faqs: [
      {
        q: "ヴィーナス ステムサップとはどのような施術ですか？",
        a: "プラズマとStemSup（幹細胞培養上清液）を組み合わせたお肌管理メニューです。",
      },
      {
        q: "シンデレラ ステムサップとの違いは何ですか？",
        a: "ヴィーナス ステムサップはプラズマ、シンデレラ ステムサップはダーマペン4を使用します。",
      },
    ],
  },

  /* ============================== 02 ヴィーナス ダイヤモンド ============================== */
  "venus-diamond": {
    about: [
      "ヴィーナス ダイヤモンドは、プラズマとリジュランを組み合わせたSBDCのお肌管理メニューです。",
      "SBDCでは、ダイヤモンド系メニューとしてリジュランを使用しています。",
    ],
    aboutNote:
      "院内では「サーモン注射」系のメニューとしてご案内する場合がありますが、SBDCで使用する製剤はリジュランです。",
    features: [
      {
        no: "01",
        title: "プラズマとリジュランを組み合わせた施術",
        text: "プラズマとリジュランを組み合わせたお肌管理メニューです。",
      },
      {
        no: "02",
        title: "肌状態に合わせて施術",
        text: "お肌の状態を確認し、施術内容をご案内します。",
      },
      {
        no: "03",
        title: "医師の診察のもとでご案内",
        text: "医師の診察のもとで施術をご案内します。",
      },
    ],
    method: [
      { label: "分類", value: "PLASMA" },
      { label: "施術方法", value: "プラズマ" },
      { label: "使用製剤", value: "リジュラン" },
    ],
    // 02 ヴィーナス ダイヤモンド: 施術時間／麻酔／ダウンタイム／推奨回数／推奨間隔は未確定（「—」を維持）。SPメニュー内容は確認中
    spec: spec(),
    price: {
      rows: [{ label: "ヴィーナス ダイヤモンド（顔）", price: "99,000円" }],
    },
    risks: [
      "施術後に赤み、熱感、刺激感、乾燥などが生じる場合があります。",
      "また、使用する製剤や肌状態によって、一時的な炎症や肌トラブルが生じる可能性があります。",
      "症状や経過には個人差があります。",
    ],
    faqs: [
      {
        q: "ヴィーナス ダイヤモンドとはどのような施術ですか？",
        a: "プラズマとリジュランを組み合わせたお肌管理メニューです。",
      },
      {
        q: "シンデレラ ダイヤモンドとの違いは何ですか？",
        a: "ヴィーナス ダイヤモンドはプラズマ、シンデレラ ダイヤモンドはダーマペン4を使用します。",
      },
    ],
  },

  /* ============================== 03 シンデレラ ステムサップ ============================== */
  "cinderella-stemsup": {
    about: [
      "シンデレラ ステムサップは、ダーマペン4とStemSup（幹細胞培養上清液）を組み合わせたSBDCのお肌管理メニューです。",
      "お肌の状態やお悩みを確認し、医師の診察のもと施術をご案内します。",
    ],
    aboutNote: "StemSupは、生きた幹細胞を移植する治療ではありません。幹細胞培養上清液を使用した美容医療です。",
    features: [
      {
        no: "01",
        title: "ダーマペン4とStemSupを組み合わせた施術",
        text: "ダーマペン4とStemSup（幹細胞培養上清液）を組み合わせたお肌管理メニューです。",
      },
      {
        no: "02",
        title: "肌状態に合わせて施術",
        text: "お肌の状態やお悩みを確認し、施術内容をご案内します。",
      },
      {
        no: "03",
        title: "医師の診察のもとでご案内",
        text: "医師の診察のもとで施術をご案内します。",
      },
    ],
    method: [
      { label: "分類", value: "DERMAPEN 4" },
      { label: "施術方法", value: "ダーマペン4" },
      { label: "使用製剤", value: "StemSup（幹細胞培養上清液）" },
    ],
    // 03 シンデレラ ステムサップ: 麻酔クリーム（別料金）は確定済み。他の4項目と料金は未確定（「—」を維持）
    spec: spec({ anesthesia: "麻酔クリーム（別料金）" }),
    price: {
      rows: [
        { label: "脂肪幹細胞由来培養上清液（StemSup-A）", price: "121,000円" },
        { label: "歯髄幹細胞由来培養上清液（StemSup-DP）", price: "121,000円" },
        { label: "臍帯幹細胞由来培養上清液（StemSup-UC）", price: "165,000円" },
      ],
    },
    risks: [
      "ダーマペン4による施術後に、赤み、腫れ、熱感、刺激感、乾燥、針跡、点状出血などが生じる場合があります。",
      "肌状態によっては、皮むけ、かさぶた、色素沈着、炎症などが生じる可能性があります。",
      "症状や経過には個人差があります。",
    ],
    faqs: [
      {
        q: "シンデレラ ステムサップとはどのような施術ですか？",
        a: "ダーマペン4とStemSup（幹細胞培養上清液）を組み合わせたお肌管理メニューです。",
      },
      {
        q: "ヴィーナス ステムサップとの違いは何ですか？",
        a: "使用する施術方法が異なり、ヴィーナス ステムサップはプラズマ、シンデレラ ステムサップはダーマペン4を使用します。",
      },
    ],
  },

  /* ============================== 04 シンデレラ ダイヤモンド ============================== */
  "cinderella-diamond": {
    about: [
      "シンデレラ ダイヤモンドは、ダーマペン4とリジュランを組み合わせたSBDCのお肌管理メニューです。",
      "お肌の状態やお悩みを確認し、医師の診察のもと施術をご案内します。",
    ],
    aboutNote:
      "院内では「サーモン注射」系のメニューとしてご案内する場合がありますが、SBDCで使用する製剤はリジュランです。",
    features: [
      {
        no: "01",
        title: "ダーマペン4とリジュランを組み合わせた施術",
        text: "ダーマペン4とリジュランを組み合わせたお肌管理メニューです。",
      },
      {
        no: "02",
        title: "肌状態に合わせたお肌管理",
        text: "お肌の状態に合わせて施術内容をご案内します。",
      },
      {
        no: "03",
        title: "医師の診察のもとでご案内",
        text: "医師の診察のもとで施術をご案内します。",
      },
    ],
    method: [
      { label: "分類", value: "DERMAPEN 4" },
      { label: "施術方法", value: "ダーマペン4" },
      { label: "使用製剤", value: "リジュラン" },
    ],
    // 04 シンデレラ ダイヤモンド: 麻酔クリーム（別料金）は確定済み。他の4項目は未確定（「—」を維持）
    spec: spec({ anesthesia: "麻酔クリーム（別料金）" }),
    price: {
      rows: [{ label: "シンデレラ ダイヤモンド（顔）", price: "154,000円" }],
      note: "麻酔クリームは別料金です。",
    },
    risks: [
      "ダーマペン4による施術後に、赤み、腫れ、熱感、刺激感、乾燥、針跡、点状出血などが生じる場合があります。",
      "また、使用する製剤や肌状態によって、一時的な炎症や肌トラブルが生じる可能性があります。",
      "症状や経過には個人差があります。",
    ],
    faqs: [
      {
        q: "シンデレラ ダイヤモンドとはどのような施術ですか？",
        a: "ダーマペン4とリジュランを組み合わせたお肌管理メニューです。",
      },
      {
        q: "ヴィーナス ダイヤモンドとの違いは何ですか？",
        a: "使用する施術方法が異なり、ヴィーナス ダイヤモンドはプラズマ、シンデレラ ダイヤモンドはダーマペン4を使用します。",
      },
    ],
  },

  /* ============================== 05 ヴェルヴェットスキン ============================== */
  "velvet-skin": {
    about: [
      "ヴェルヴェットスキンは、ダーマペン4とコラーゲンピールを組み合わせた施術です。",
      "お肌の状態やお悩みを確認し、医師の診察のもと施術をご案内します。",
    ],
    features: [
      {
        no: "01",
        title: "ダーマペン4とコラーゲンピールを組み合わせる施術",
        text: "ダーマペン4で肌に微細な刺激を与えた後、コラーゲンピールを組み合わせて施術を行います。",
      },
      {
        no: "02",
        title: "複数の肌悩みについて相談される施術",
        text: "毛穴、ハリ、くすみ、小じわ、ニキビ跡など、複数の肌悩みについて相談される施術です。効果の現れ方には個人差があります。",
      },
      {
        no: "03",
        title: "肌状態に合わせて施術",
        text: "ダーマペン4は針の深さを調整できる機器です。SBDCでは肌状態や目的を確認し、医師の診察のもと施術をご案内します。",
      },
      {
        no: "04",
        title: "麻酔クリームを使用可能",
        text: "施術時は麻酔クリームを使用できます。SBDCでは麻酔クリームは別料金です。",
      },
    ],
    method: [
      { label: "分類", value: "DERMAPEN 4" },
      { label: "施術方法", value: "ダーマペン4＋コラーゲンピール" },
      { label: "使用製剤", value: "コラーゲンピール" },
    ],
    // 05 ヴェルヴェットスキン: 麻酔クリーム表記は維持。施術時間／ダウンタイム／推奨回数／推奨間隔は未確定（「—」を維持）
    spec: spec({ anesthesia: "麻酔クリーム" }),
    price: {
      rows: [{ label: "顔全体", price: "99,000円" }],
    },
    risks: [
      "赤み、腫れ、刺激感、乾燥、皮むけ等が生じる場合があります。",
      "症状には個人差があります。",
    ],
    riskNote: "美容医療は自由診療です。施術前に医師より説明を行います。",
    faqs: [
      {
        q: "ヴェルヴェットスキンとは何ですか？",
        a: "ダーマペン4とコラーゲンピールを組み合わせた施術です。",
      },
    ],
  },

  /* ============================== 06 ラクトピール ============================== */
  "lacto-peel": {
    about: [
      "ラクトピールは、乳酸を使用したケミカルピーリングです。",
      "乳酸はAHAの一種です。",
      "お肌の状態を確認し、医師の診察のもと施術をご案内します。",
    ],
    features: [
      {
        no: "01",
        title: "乳酸を使用したケミカルピーリング",
        text: "ラクトピールは、乳酸を使用して肌表面の角質にアプローチするピーリング施術です。乳酸はAHAの一種です。",
      },
      {
        no: "02",
        title: "比較的マイルドなピーリング",
        text: "乳酸は分子が比較的大きく、皮膚表面側に作用しやすいとされています。刺激に配慮しながらお肌管理を行いたい方にも検討される施術です。",
      },
      {
        no: "03",
        title: "顔だけでなく複数部位に対応",
        text: "SBDCでは、顔全体・胸部／デコルテ・背中・臀部・上腕・両ワキに料金設定があります。",
      },
      {
        no: "04",
        title: "継続的なお肌管理として相談可能",
        text: "肌状態や目的に合わせて、継続的なお肌管理の一つとしてご相談いただけます。具体的な施術回数や間隔は、肌状態を確認したうえでご案内します。",
      },
    ],
    method: [
      { label: "分類", value: "PEELING" },
      { label: "施術方法", value: "ケミカルピーリング" },
      { label: "使用製剤", value: "乳酸系ピーリング" },
    ],
    // 06 ラクトピール: 施術時間／麻酔／ダウンタイム／推奨回数／推奨間隔は未確定（「—」を維持）
    spec: spec(),
    price: {
      rows: [
        { label: "顔全体", price: "22,000円" },
        { label: "胸部／デコルテ", price: "33,000円" },
        { label: "背中上部 or 背中下部", price: "33,000円" },
        { label: "臀部", price: "33,000円" },
        { label: "上腕", price: "33,000円" },
        { label: "両ワキ", price: "33,000円" },
      ],
    },
    risks: ["赤み、刺激感、乾燥、皮むけ等が生じる場合があります。", "症状には個人差があります。"],
    riskNote: "美容医療は自由診療です。施術前に医師より説明を行います。",
  },

  /* ============================== 07 コラーゲンピール ============================== */
  "collagen-peel": {
    about: [
      "コラーゲンピールは、ピーリング剤を肌に塗布するお肌管理メニューです。",
      "お肌の状態を確認し、医師の診察のもと施術をご案内します。",
    ],
    features: [
      {
        no: "01",
        title: "薬剤を塗布して行うピーリング施術",
        text: "コラーゲンピールは、薬剤を肌に塗布しながら行うピーリング施術です。",
      },
      {
        no: "02",
        title: "複数の肌悩みについて相談される施術",
        text: "毛穴、ハリ、ツヤ、小じわ、くすみなど、複数の肌悩みについて相談される施術です。効果の現れ方には個人差があります。",
      },
      {
        no: "03",
        title: "ダウンタイムに配慮した肌管理として検討される施術",
        text: "赤みや皮むけ、刺激感が生じる場合がありますが、継続的なお肌管理の一つとして検討される施術です。",
      },
      {
        no: "04",
        title: "顔・首・デコルテに対応",
        text: "SBDCでは、顔全体・首・デコルテに料金設定があります。",
      },
    ],
    method: [
      { label: "分類", value: "PEELING" },
      { label: "施術方法", value: "ピーリング" },
      { label: "使用製剤", value: "コラーゲンピール" },
    ],
    // 07 コラーゲンピール: 施術時間／麻酔／ダウンタイム／推奨回数／推奨間隔は未確定（「—」を維持）
    spec: spec(),
    price: {
      rows: [
        { label: "顔全体", price: "33,000円" },
        { label: "首", price: "33,000円" },
        { label: "デコルテ", price: "33,000円" },
      ],
    },
    risks: ["赤み、刺激感、乾燥、皮むけ等が生じる場合があります。", "症状には個人差があります。"],
    riskNote: "美容医療は自由診療です。施術前に医師より説明を行います。",
  },

  /* ============================== 08 MIXピール ============================== */
  "mix-peel": {
    about: [
      "MIXピールは、複数のピーリング成分を組み合わせたケミカルピーリングです。",
    ],
    features: [
      {
        no: "01",
        title: "複数のピーリング成分を組み合わせた施術",
        text: "MIXピールは、複数のピーリング成分を組み合わせたケミカルピーリングです。",
      },
      {
        no: "02",
        title: "異なる特性を持つ成分を組み合わせる設計",
        text: "成分ごとの特性を組み合わせながら、肌表面の角質ケアを行います。",
      },
      {
        no: "03",
        title: "刺激感に配慮したピーリングとして検討される施術",
        text: "従来のピーリングで刺激感や赤み、皮むけが気になった方にも、検討される施術として案内されることがあります。効果や反応には個人差があります。",
      },
      {
        no: "04",
        title: "顔以外の複数部位にも対応",
        text: "SBDCでは、顔全体・胸部／デコルテ・背中・臀部・上腕・両ワキに料金設定があります。",
      },
    ],
    method: [
      { label: "分類", value: "PEELING" },
      { label: "施術方法", value: "ケミカルピーリング" },
      { label: "使用製剤", value: "MIXピール" },
    ],
    // 08 MIXピール: 施術時間／麻酔／ダウンタイム／推奨回数／推奨間隔は未確定（「—」を維持）
    spec: spec(),
    price: {
      rows: [
        { label: "顔全体", price: "22,000円" },
        { label: "胸部／デコルテ", price: "33,000円" },
        { label: "背中上部 or 背中下部", price: "33,000円" },
        { label: "臀部", price: "33,000円" },
        { label: "上腕", price: "33,000円" },
        { label: "両ワキ", price: "33,000円" },
      ],
    },
    risks: ["赤み、刺激感、乾燥、皮むけ、かぶれ等が生じる場合があります。", "症状には個人差があります。"],
    riskNote: "美容医療は自由診療です。施術前に医師より説明を行います。",
  },

  /* ============================== 09 プラズマ ============================== */
  plasma: {
    about: [
      "プラズマを用いたお肌管理メニューです。",
    ],
    features: [
      {
        no: "01",
        title: "プラズマを利用したお肌管理",
        text: "プラズマを肌に作用させる、美容目的のお肌管理メニューです。",
      },
      {
        no: "02",
        title: "肌状態に合わせて施術",
        text: "肌の状態やお悩みを確認しながら、医師の診察のもと施術をご案内します。",
      },
      {
        no: "03",
        title: "他のお肌管理メニューとの組み合わせにも対応可能",
        text: "プラズマ施術は、美容成分の導入などと組み合わせて使用されることがあります。SBDCで実際に使用する製剤や組み合わせについては、各メニューの正式情報をご案内します。",
      },
      {
        no: "04",
        title: "顔・首に対応",
        text: "SBDCでは、顔全体・首に料金設定があります。",
      },
    ],
    method: [
      { label: "分類", value: "PLASMA" },
      { label: "施術方法", value: "プラズマ" },
      { label: "使用機器", value: "確認中" },
    ],
    // 09 プラズマ: 施術時間／麻酔／ダウンタイム／推奨回数／推奨間隔は未確定（「—」を維持）。使用機器名も未確定（_methodの「使用機器」行は非表示となる）
    spec: spec(),
    price: {
      rows: [
        { label: "顔全体", price: "33,000円" },
        { label: "首", price: "33,000円" },
      ],
    },
    risks: ["赤み、刺激感、熱感等が生じる場合があります。", "症状には個人差があります。"],
    riskNote: "美容医療は自由診療です。施術前に医師より説明を行います。",
  },

  /* ============================== 10 ホワイトフォトフェイシャル ============================== */
  "white-photo-facial": {
    about: [
      "光エネルギーを肌に照射する、フォトフェイシャル系のお肌管理メニューです。",
      "「ホワイト」という名称は、必ず肌が白くなることを意味するものではありません。効果や経過には個人差があります。",
    ],
    features: [
      {
        no: "01",
        title: "光エネルギーを利用した肌治療",
        text: "光エネルギーを肌に照射する、フォトフェイシャル系のお肌管理メニューです。",
      },
      {
        no: "02",
        title: "複数の肌悩みに使用される光治療",
        text: "シミ、くすみ、赤み、色むらなど、複数の肌悩みについて相談される光治療です。肌状態や反応には個人差があります。",
      },
      {
        no: "03",
        title: "顔全体を対象としたお肌管理",
        text: "一点だけを対象にするのではなく、顔全体への施術としてご案内するメニューです。",
      },
      {
        no: "04",
        title: "「ホワイト」は効果保証を意味しない",
        text: "「ホワイトフォトフェイシャル」という名称は、必ず肌が白くなることや美白効果を保証するものではありません。肌状態や反応には個人差があります。",
      },
    ],
    method: [
      { label: "分類", value: "PHOTO FACIAL" },
      { label: "施術方法", value: "光治療" },
      { label: "使用機器", value: "確認中" },
    ],
    // 10 ホワイトフォトフェイシャル: 施術時間／麻酔／ダウンタイム／推奨回数／推奨間隔は未確定（「—」を維持）。使用機器名も未確定（_methodの「使用機器」行は非表示となる）
    spec: spec(),
    price: {
      rows: [
        { label: "フォト＋MIXピール", price: "77,000円" },
        { label: "フォト＋乳酸ピール", price: "77,000円" },
      ],
    },
    risks: ["赤み、熱感、刺激感等が生じる場合があります。", "症状には個人差があります。"],
    riskNote: "美容医療は自由診療です。施術前に医師より説明を行います。",
  },
};